export type UploadStatus = "queued" | "uploading" | "uploaded" | "error";

/**
 * A single file queued for upload. Owns only transfer state — user metadata
 * (description/filename) and UI previews live outside this model.
 */
export class UploadItem {
  readonly id: string;

  // Mutable so callers can swap in a processed file (e.g. a resized image).
  file: File;

  status: UploadStatus = "queued";
  progress = 0;
  error?: string;
  response?: unknown;

  // Optional user-entered metadata sent with the upload.
  description?: string;
  filename?: string;

  constructor(file: File, id: string = UploadItem.nextId()) {
    this.file = file;
    this.id = id;
  }

  get name(): string {
    return this.file.name;
  }

  get size(): number {
    return this.file.size;
  }

  get type(): string {
    return this.file.type;
  }

  get isQueued(): boolean {
    return this.status === "queued";
  }

  get isUploading(): boolean {
    return this.status === "uploading";
  }

  get isUploaded(): boolean {
    return this.status === "uploaded";
  }

  get isError(): boolean {
    return this.status === "error";
  }

  private static counter = 0;

  private static nextId(): string {
    const uuid = globalThis.crypto?.randomUUID?.();
    return uuid ?? `upload-${Date.now()}-${UploadItem.counter++}`;
  }
}
